import { httpApi } from '@app/api/http.api'

export type AuthData = {
  email: string
  password: string
}

export type ResetPasswordRequest = {
  email: string
  language: string
}

export type ValidateTokenRequest = {
  token: string
}

export type ValidateTokenResponse = {
  valid: boolean
}

export type NewPasswordRequest = {
  token: string
  password: string
}

export type LoginRequest = {
  email: string
  password: string
}

export type LoginResponse = {
  id: string
  consentRequired?: boolean
}

export type LoginMfaSetupResponse = {
  mfaSetupRequired: true
  setupToken: string
}

export type LoginMfaRequiredResponse = {
  mfaRequired: true
  mfaToken: string
  method: string
  availableMethods?: string[]
}

export type LoginUnionResponse = LoginResponse | LoginMfaSetupResponse | LoginMfaRequiredResponse

export type MfaVerifyRequest = {
  mfaToken: string
  method: 'TOTP' | 'EMAIL' | 'BACKUP_CODE'
  code: string
}

export type EmailMfaSendRequest = {
  mfaToken: string
  language?: string
}

export type EmailMfaSendResponse = {
  emailMfaToken: string
}

export type MfaSetupRequest = {
  setupToken: string
}

export type MfaSetupResponse = {
  otpauthUrl: string
  secret: string
}

export type MfaEnableRequest = {
  setupToken: string
  code: string
}

export type MfaEnableResponse = {
  success: true
  backupCodes: string[]
  id: string
  consentRequired?: boolean
}

export type MfaRegenerateRequest = {
  verifyToken: string
  code: string
  method: 'TOTP' | 'EMAIL' | 'BACKUP_CODE'
}

export type MfaRegenerateResponse = {
  backupCodes: string[]
}

export type MfaEmailEnableRequest = {
  setupToken: string
  emailMfaToken: string
  code: string
}

export type MfaEmailEnableResponse = {
  success: true
  backupCodes: string[]
  id: string
  consentRequired?: boolean
}

export const login = (loginPayload: LoginRequest): Promise<LoginUnionResponse> =>
  httpApi.post<LoginUnionResponse>('auth/signin', { ...loginPayload }).then(({ data }) => data)

export const resetPassword = (resetPasswordPayload: ResetPasswordRequest): Promise<{ success: boolean }> =>
  httpApi.post<{ success: boolean }>('auth/forgot-password', { ...resetPasswordPayload }).then(({ data }) => data)

export const validateToken = (payload: ValidateTokenRequest): Promise<ValidateTokenResponse> =>
  httpApi.post<ValidateTokenResponse>('auth/validate-token', { ...payload }).then(({ data }) => data)

export const newPassword = (payload: NewPasswordRequest): Promise<{ success: boolean }> =>
  httpApi.post<{ success: boolean }>('auth/reset-password', { ...payload }).then(({ data }) => data)

export type InitiateMethodChangeResponse = {
  verifyToken: string
  currentMethod: string
}

export type MethodChangeConfirmRequest = {
  verifyToken: string
  method: 'TOTP' | 'EMAIL' | 'BACKUP_CODE'
  code: string
}

export type MethodChangeConfirmResponse = {
  setupToken: string
}

/** Wywołania niosące kod MFA w ciele — ich 4xx dotyczy treści żądania, nie sesji.
 *  `initiateMethodChange` celowo bez tego: to zwykłe wywołanie chronione sesją. */
const MFA_CREDENTIAL_REQUEST = { skipAuthRefresh: true } as const

export const verifyMfa = (payload: MfaVerifyRequest): Promise<LoginResponse> =>
  httpApi.post<LoginResponse>('auth/mfa/verify', { ...payload }, MFA_CREDENTIAL_REQUEST).then(({ data }) => data)

export const sendEmailMfaCode = (payload: EmailMfaSendRequest): Promise<EmailMfaSendResponse> =>
  httpApi
    .post<EmailMfaSendResponse>('auth/mfa/email/send', { ...payload }, MFA_CREDENTIAL_REQUEST)
    .then(({ data }) => data)

export const enableEmailMfa = (payload: MfaEmailEnableRequest): Promise<MfaEmailEnableResponse> =>
  httpApi
    .post<MfaEmailEnableResponse>('auth/mfa/email/enable', { ...payload }, MFA_CREDENTIAL_REQUEST)
    .then(({ data }) => data)

export const initTotpSetup = (payload: MfaSetupRequest): Promise<MfaSetupResponse> =>
  httpApi.post<MfaSetupResponse>('auth/mfa/setup', { ...payload }).then(({ data }) => data)

export const enableTotp = (payload: MfaEnableRequest): Promise<MfaEnableResponse> =>
  httpApi.post<MfaEnableResponse>('auth/mfa/enable', { ...payload }, MFA_CREDENTIAL_REQUEST).then(({ data }) => data)

export const initiateMethodChange = (): Promise<InitiateMethodChangeResponse> =>
  httpApi.post<InitiateMethodChangeResponse>('auth/mfa/change-method/initiate').then(({ data }) => data)

export const confirmMethodChange = (payload: MethodChangeConfirmRequest): Promise<MethodChangeConfirmResponse> =>
  httpApi
    .post<MethodChangeConfirmResponse>('auth/mfa/change-method', { ...payload }, MFA_CREDENTIAL_REQUEST)
    .then(({ data }) => data)

export const regenerateBackupCodes = (payload: MfaRegenerateRequest): Promise<MfaRegenerateResponse> =>
  httpApi
    .post<MfaRegenerateResponse>('auth/mfa/backup-codes/regenerate', { ...payload }, MFA_CREDENTIAL_REQUEST)
    .then(({ data }) => data)

export const logout = (): Promise<{ success: true }> =>
  httpApi.post<{ success: true }>('auth/logout').then(({ data }) => data)
